import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  FormControl,
  Select,
  MenuItem,
  FormHelperText,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useMediaQuery, useTheme } from "@mui/material";
import Color from "../../components/Color";
import { getAllCategoryLists } from "../../service/categoryService";
import { useSnackbar } from "../../App";

const SubCategory = ({ open, onClose, formData, setFormData, onSubmit }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const nameInputRef = useRef(null);

  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const categoryRes = await getAllCategoryLists();
        setCategories(
          Array.isArray(categoryRes?.data?.data) ? categoryRes.data.data : []
        );
      } catch (error) {
        showSnackbar("Failed to load categories", "error");
      }
    };
    if (open) {
      fetchDropdowns();
    }
  }, [open, showSnackbar]);

  const validate = () => {
    const newErrors = {};

    if (!formData.category_id) {
      newErrors.category_id = ["Category is required"];
    }

    if (!formData.name?.trim()) {
      newErrors.name = ["Subcategory name is required"];
    } else if (!/^[a-zA-Z ]+$/.test(formData.name)) {
      newErrors.name = ["Subcategory name must contain only letters"];
    } else if (formData.name.length > 50) {
      newErrors.name = ["Subcategory name must not exceed 50 characters"];
    }

    if (formData.description && formData.description.length > 250) {
      newErrors.description = ["Description must not exceed 250 characters"];
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ==================== FIXED handleSubmit ====================
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showSnackbar("Please fix validation errors before submitting.", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await onSubmit(); // ← This returns { status: "success", data: {...} }

      if (response?.status === "success") {
        showSnackbar(
          formData.id
            ? "Subcategory updated successfully!"
            : "Subcategory created successfully!",
          "success"
        );
        handleCancel();
      } 
      else if (response?.status === "error" && response?.errors) {
        // Backend returned validation errors
        const formattedErrors = {};
        Object.keys(response.errors).forEach((field) => {
          formattedErrors[field] = response.errors[field];
        });

        setErrors((prev) => ({ ...prev, ...formattedErrors }));

        if (formattedErrors.name && nameInputRef.current) {
          nameInputRef.current.focus();
        }

        showSnackbar(response.message || "Validation failed", "error");
      } 
      else {
        // Unexpected response format
        showSnackbar(response?.message || "Something went wrong", "error");
      }
    } catch (err) {
      console.error("Submit failed:", err);

      if (err.response?.data) {
        const data = err.response.data;

        if (data.status === "error" && data.errors) {
          const formattedErrors = {};
          Object.keys(data.errors).forEach((field) => {
            formattedErrors[field] = data.errors[field];
          });

          setErrors((prev) => ({ ...prev, ...formattedErrors }));

          if (formattedErrors.name && nameInputRef.current) {
            nameInputRef.current.focus();
          }

          showSnackbar(data.message || "Validation failed", "error");
          return;
        }
      }

      showSnackbar("Server error. Please try again later.", "error");
    } finally {
      setLoading(false);
    }
  };
  // ===========================================================

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      category_id: "",
      is_active: true,
    });
    setErrors({});
    onClose();
  };

  const handleChange = (eOrName, value) => {
    if (typeof eOrName === "string") {
      setFormData((prev) => ({ ...prev, [eOrName]: value }));
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[eOrName];
        return newErrors;
      });
    } else {
      const { name, value: inputValue } = eOrName.target;

      if (name === "name") {
        const isValid = /^[a-zA-Z ]*$/.test(inputValue);
        const filtered = inputValue.replace(/[^a-zA-Z ]/g, "");
        const limitedValue = filtered.slice(0, 50);

        setFormData((prev) => ({ ...prev, name: limitedValue }));

        if (!isValid) {
          setErrors((prev) => ({ ...prev, name: ["Only letters are allowed"] }));
        } else if (limitedValue.length === 50 && inputValue.length > 50) {
          setErrors((prev) => ({
            ...prev,
            name: ["Subcategory name must not exceed 50 characters"],
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.name;
            return newErrors;
          });
        }
      } else if (name === "description") {
        const limitedValue = inputValue.slice(0, 250);
        setFormData((prev) => ({ ...prev, description: limitedValue }));

        if (inputValue.length > 250) {
          setErrors((prev) => ({
            ...prev,
            description: ["Description must not exceed 250 characters"],
          }));
        } else {
          setErrors((prev) => {
            const newErrors = { ...prev };
            delete newErrors.description;
            return newErrors;
          });
        }
      } else {
        setFormData((prev) => ({ ...prev, [name]: inputValue }));
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (loading) return;
        handleCancel();
      }}
      disableEscapeKeyDown={loading}
      PaperProps={{
        sx: {
          width: isMobile ? "90vw" : "400px",
          maxWidth: "95vw",
          borderRadius: "8px",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: isMobile ? "1rem" : "1.25rem",
          position: "relative",
          pr: 6,
        }}
      >
        {formData.id ? "Edit Subcategory" : "Add Subcategory"}

        <IconButton
          aria-label="close"
          onClick={handleCancel}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: isMobile ? 2 : 3,
          py: isMobile ? 1 : 2,
          maxHeight: isMobile ? "70vh" : "auto",
          overflowY: "auto",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 2 }}>
            {/* Category Dropdown */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Category*
              </Typography>
              <FormControl fullWidth error={!!errors.category_id}>
                <Select
                  value={formData.category_id ?? ""}
                  onChange={(e) => handleChange("category_id", e.target.value)}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: "#9e9e9e" }}>Select category</span>;
                    }
                    return categories.find((cat) => cat.id === selected)?.name || "";
                  }}
                  sx={{
                    "& .MuiSelect-select": {
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    },
                    height: 40,
                  }}
                >
                  <MenuItem value="" disabled>
                    Select category
                  </MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.category_id && (
                  <FormHelperText>{errors.category_id[0]}</FormHelperText>
                )}
              </FormControl>
            </Box>

            {/* Subcategory Name */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Subcategory Name*
              </Typography>
              <TextField
                name="name"
                value={formData.name || ""}
                onChange={handleChange}
                placeholder="Enter subcategory name"
                fullWidth
                error={!!errors.name}
                helperText={
                  errors.name?.[0] || `${formData.name?.length || 0}/50`
                }
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputRef={nameInputRef}
                inputProps={{ maxLength: 50 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-input": {
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  },
                }}
              />
            </Box>

            {/* Description */}
            <Box>
              <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Description (Optional)
              </Typography>
              <TextField
                name="description"
                value={formData.description || ""}
                onChange={handleChange}
                placeholder="Enter description"
                fullWidth
                error={!!errors.description}
                helperText={
                  errors.description?.[0] ||
                  `${formData.description?.length || 0}/250`
                }
                multiline
                rows={4}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ maxLength: 250 }}
                sx={{
                  "& .MuiInputLabel-root": { display: "none" },
                  "& .MuiInputBase-inputMultiline": {
                    paddingTop: "8px",
                    paddingBottom: "8px",
                    lineHeight: 1.5,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  },
                }}
              />
            </Box>

            {/* Active Status */}
            <Box>
               <Typography
                variant="body1"
                sx={{
                  mb: 1,
                  fontWeight: "bold",
                  color: Color.label.main,
                  fontSize: isMobile ? "12px" : "13px",
                }}
              >
                Status
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active ?? true}
                    onChange={(e) => handleChange("is_active", e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: Color.primary.main,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: Color.primary.main,
                      },
                      "& .MuiSwitch-track": {
                        backgroundColor: "#ccc",
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      color: formData.is_active ? Color.primary.main : "text.secondary",
                      fontWeight: formData.is_active ? "bold" : "normal",
                      fontSize: isMobile ? "0.85rem" : "1rem",
                    }}
                  >
                    {formData.is_active ? "Active" : "Inactive"}
                  </Typography>
                }
              />
            </Box>
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={2} mt={3} mb={1}>
            <Button
              onClick={handleCancel}
              sx={{
                background: "none",
                color: Color.secondary.main,
                border: "1px solid",
                borderColor: Color.secondary.main,
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              sx={{
                background: Color.primary.main,
                color: "white",
                width: isMobile ? "100%" : "auto",
                height: isMobile ? "40px" : "45px",
                fontSize: isMobile ? "0.85rem" : "1rem",
                padding: isMobile ? "8px" : "6px 16px",
                "&:hover": {
                  background: Color.primary.dark,
                },
                "&.Mui-disabled": {
                  background: Color.primary.main,
                  opacity: 0.7,
                  color: "white",
                },
              }}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: "white" }} />
              ) : formData.id ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Box>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SubCategory;